import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

class MusherRatingStore extends LocalModel {
    constructor() {
        super('musherRating', {
            namePlural: 'musherRatings',
            namePluralReal: 'musherratings',
            sort: '-rating',
            limit: 100,
            api: {
                search: {
                    url: '/api/musherratings/search',
                    params: {
                        limit: 20,
                    },
                },
                load: {
                    url: '/api/musherratings/',
                    params: {},
                },
                save: {
                    url: '/api/musherratings/',
                    params: {},
                },
            },
        });
    }

    @observable musherRatings = [];

    @observable musherRating = {};

    @observable leaderboard = [];

    @observable selectedYear = null;

    @observable years = [];

    @observable config = {};

    @observable isLoading = false;

    @observable searchResults = [];

    @action
    setSelectedYear = (year) => {
        this.selectedYear = year;
    }

    @action
    setIsLoading = (loading) => {
        this.isLoading = loading;
    }

    /**
     * Load leaderboard with optional year filter
     */
    loadLeaderboard = async (year = null, limit = 100, offset = 0) => {
        this.setIsLoading(true);
        try {
            const params = { limit, offset };
            if (year) {
                params.year = year;
            }
            const response = await util.fetchApi('/api/musherratings/public', { method: 'GET' }, params);
            if (response.status === 200) {
                this.updateKeyValue('leaderboard', response.data || []);
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error loading leaderboard:', error);
            return [];
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Load a musher's detailed rating by ID
     */
    loadMusherRating = async (id) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/musherratings/${id}`, { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('musherRating', response.data || {});
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error loading musher rating:', error);
            return null;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Load a musher's rating by user ID
     */
    loadMusherRatingByUserId = async (userId) => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi(`/api/musherratings/user/${userId}`, { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('musherRating', response.data || {});
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error loading musher rating by user:', error);
            return null;
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * Load available years
     */
    loadYears = async () => {
        try {
            const response = await util.fetchApi('/api/musherratings/years', { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('years', response.data || []);
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error loading years:', error);
            return [];
        }
    }

    /**
     * Load sponsor configuration
     */
    loadConfig = async () => {
        try {
            const response = await util.fetchApi('/api/musherratings/config', { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('config', response.data || {});
                return response.data;
            }
            return {};
        } catch (error) {
            console.error('Error loading config:', error);
            return {};
        }
    }

    /**
     * Search for mushers by name
     */
    searchMusher = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            this.updateKeyValue('searchResults', []);
            return [];
        }
        try {
            const response = await util.fetchApi('/api/musherratings/search', { method: 'GET' }, { q: searchTerm });
            if (response.status === 200) {
                this.updateKeyValue('searchResults', response.data || []);
                return response.data;
            }
            return [];
        } catch (error) {
            console.error('Error searching mushers:', error);
            return [];
        }
    }

    /**
     * Trigger full recalculation (admin only)
     */
    recalculateRatings = async () => {
        this.setIsLoading(true);
        try {
            const response = await util.fetchApi('/api/musherratings/recalculate', { method: 'POST' });
            if (response.status === 200) {
                return response.data;
            }
            throw new Error(response.message || 'Recalculation failed');
        } catch (error) {
            console.error('Error recalculating ratings:', error);
            throw error;
        } finally {
            this.setIsLoading(false);
        }
    }
}

const store = new MusherRatingStore();
export default store;
